import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { deleteTag, listCuratedTags, mergeTag, renameTag, type CuratedTag } from "../api";
import BackLink from "../components/BackLink";
import ConfirmDelete from "../components/ConfirmDelete";
import MutationError from "../components/MutationError";
import Placeholder, { Pending } from "../components/Placeholder";
import { useT } from "../i18n";
import { refetch } from "../refetch";
import { useSession } from "../session";

const inputClass =
  "w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-sm focus:border-sage focus:outline-none";
const pillClass =
  "shrink-0 rounded-full border border-line px-3 py-1 text-xs font-medium hover:border-sage disabled:opacity-40";

/** Reads the lists again after a write (`refetch` says why it cancels first). */
function refetchTags(client: QueryClient) {
  return refetch(client, ["admin", "tags"], ["tags"], ["books"]);
}

/**
 * AdminTags is where a platform admin keeps the one shared list of tags tidy
 * (requirement 0119 T3, ADR-0185): rename one, merge it into another, or
 * delete it, on every book that carries it. Making and setting tags is the
 * studio's, on a book.
 */
export default function AdminTags() {
  const { me, isPending } = useSession();
  const t = useT();
  const isAdmin = !!me?.user.platform_admin;
  const list = useQuery({
    queryKey: ["admin", "tags"],
    queryFn: listCuratedTags,
    enabled: isAdmin,
  });

  if (!isPending && !isAdmin) return <Navigate to="/" replace />;
  const items = list.data?.items ?? [];

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <BackLink to="/admin">{t.admin.back}</BackLink>
      <header className="mt-4 mb-6">
        <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight">{t.tag.title}</h1>
        <p className="mt-1 text-sm text-muted">{t.tag.what}</p>
      </header>

      {(isPending || list.isPending) && (
        <Pending label={t.common.loading}>
          <Placeholder className="h-40 rounded-card" />
        </Pending>
      )}
      {list.isError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t.tag.listFailed}{" "}
          <button type="button" onClick={() => void list.refetch()} className="underline">
            {t.common.tryAgain}
          </button>
        </p>
      )}
      {list.data && items.length === 0 && (
        <p data-testid="tags-empty" className="text-sm text-muted">
          {t.tag.empty}
        </p>
      )}
      <ul className="divide-y divide-line rounded-card border border-line bg-surface empty:hidden">
        {items.map((tag) => (
          <TagRow key={tag.id} tag={tag} others={items.filter((o) => o.id !== tag.id)} />
        ))}
      </ul>
    </main>
  );
}

function TagRow({ tag, others }: { tag: CuratedTag; others: CuratedTag[] }) {
  const t = useT();
  const client = useQueryClient();
  const [name, setName] = useState(tag.name);
  const [merging, setMerging] = useState(false);
  const refresh = () => refetchTags(client);
  const rename = useMutation({ mutationFn: () => renameTag(tag.id, name), onSuccess: refresh });
  const merge = useMutation({
    mutationFn: (into: string) => mergeTag(tag.id, into),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: () => deleteTag(tag.id), onSuccess: refresh });
  const changed = name.trim() !== tag.name && name.trim() !== "";

  return (
    <li data-testid="admin-tag" data-name={tag.name} className="space-y-2 px-3 py-3">
      <div className="flex items-center gap-2">
        <input
          dir="auto"
          aria-label={t.tag.rename}
          data-testid="admin-tag-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClass}
        />
        <span className="shrink-0 text-xs text-muted tabular-nums">{t.tag.books(tag.books)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="admin-tag-rename"
          disabled={!changed || rename.isPending}
          onClick={() => rename.mutate()}
          className={pillClass}
        >
          {t.tag.rename}
        </button>
        <button
          type="button"
          data-testid="admin-tag-merge"
          aria-pressed={merging}
          disabled={others.length === 0}
          onClick={() => setMerging(!merging)}
          className={pillClass}
        >
          {merging ? t.tag.mergeCancel : t.tag.merge}
        </button>
        <ConfirmDelete
          label={t.tag.delete}
          question={t.tag.deleteQuestion(tag.name)}
          onConfirm={() => remove.mutate()}
          testId="admin-tag-delete"
        />
      </div>
      {merging && (
        <div className="flex flex-wrap gap-2" data-testid="admin-tag-merge-into">
          {others.map((o) => (
            <button
              key={o.id}
              type="button"
              dir="auto"
              aria-label={t.tag.mergeInto(o.name)}
              data-testid="admin-tag-merge-target"
              disabled={merge.isPending}
              onClick={() => merge.mutate(o.id)}
              className={pillClass}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}
      <MutationError
        error={rename.error ?? merge.error ?? remove.error}
        fallback={t.common.somethingWrong}
      />
    </li>
  );
}
